import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {GridAreaItem} from '../../../../libs/shared/ui/src/components/grid/grid';
import {AsyncLoader} from '../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {LoaderVariantsEnum} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {Skeleton} from '../../../../libs/shared/ui/src/components/utility/skeleton';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*

 * Props.
 */

interface ViewerLoaderProps {
  topbar: ReactNode;
  header: ReactNode;
  status: AsyncStatusesEnum;
  onRetryClick: () => void;
}

/*
 * Style.
 */

const StyledLoaderDiv = styled.div`
  grid-row: 1 / span 4;
  grid-column: 1;
  pointer-events: none;

  & > * {
    pointer-events: all;
  }

  ${center()};
`;

const StyledNewLoaderDiv = styled.div`
  grid-row: 1 / span 4;
  grid-column: 1;
  pointer-events: none;

  display: grid;
  grid-template-areas:
    '.'
    'topbar'
    '.'
    'header'
    '.'
    'message'
    '.'
    'comments'
    '.'
    'composer'
    '.';
  grid-template-rows: 8px auto 7px auto 14px auto 13px auto 1fr auto 18px;
  padding-left: 6px;
  padding-right: 14px;
`;

const StyledNewLoaderTopbar = styled.div`
  grid-area: topbar;
  display: grid;
  grid-template-areas: 'move . action1 . action2 . action3 . assign';
  grid-template-columns: auto 1fr auto 8px auto 8px auto 8px auto;
  align-items: center;
`;

const StyledNewLoaderHeader = styled.div`
  grid-area: header;
  display: grid;
  grid-template-areas: 'subject . ';
  grid-template-columns: auto 1fr;
  align-items: center;
`;

const StyledCommentsDiv = styled.div`
  grid-area: comments;
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: 30px;
  grid-row-gap: 16px;
  align-items: flex-start;
`;
const StyledCommentDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 8px;
  justify-content: flex-start;
`;

const StyledComposerDiv = styled.div`
  grid-area: composer;
  display: grid;
  grid-template-areas: 'avatar . input';
  grid-template-columns: auto 8px 1fr;
`;

/*
 * Component.
 */

export const ViewerLoader: FC<ViewerLoaderProps> = ({status, topbar, header, onRetryClick}) => {
  const theme = useUserTheme();
  if (status !== AsyncStatusesEnum.FAILED) {
    return (
      <StyledNewLoaderDiv data-testid="loader">
        <StyledNewLoaderTopbar>
          <GridAreaItem area="move">
            <Skeleton borderRadius="4px" width="90px" height="20px" />
          </GridAreaItem>
          <GridAreaItem area="action1">
            <Skeleton borderRadius="8px" width="30px" height="30px" />
          </GridAreaItem>
          <GridAreaItem area="action2">
            <Skeleton borderRadius="8px" width="30px" height="30px" />
          </GridAreaItem>
          <GridAreaItem area="action3">
            <Skeleton borderRadius="8px" width="30px" height="30px" />
          </GridAreaItem>
          <GridAreaItem area="assign">
            <Skeleton borderRadius="100px" width="90px" height="30px" />
          </GridAreaItem>
        </StyledNewLoaderTopbar>
        <StyledNewLoaderHeader>
          <GridAreaItem area="subject">
            <Skeleton borderRadius="4px" width="178px" height="20px" />
          </GridAreaItem>
        </StyledNewLoaderHeader>
        <GridAreaItem area="message">
          <Skeleton borderRadius="16px" width="100%" height="200px" />
        </GridAreaItem>
        <StyledCommentsDiv>
          <StyledCommentDiv>
            <Skeleton borderRadius="30px" width="30px" height="30px" />
            <Skeleton borderRadius="30px" width="200px" height="30px" />
          </StyledCommentDiv>
          <StyledCommentDiv>
            <Skeleton borderRadius="30px" width="30px" height="30px" />
            <Skeleton borderRadius="30px" width="305px" height="30px" />
          </StyledCommentDiv>
        </StyledCommentsDiv>
        <StyledComposerDiv>
          <GridAreaItem area="avatar">
            <Skeleton borderRadius="30px" width="30px" height="30px" />
          </GridAreaItem>
          <GridAreaItem area="input">
            <Skeleton borderRadius="30px" width="100%" height="30px" />
          </GridAreaItem>
        </StyledComposerDiv>
      </StyledNewLoaderDiv>
    );
  }

  return (
    <>
      {topbar}
      {header}
      <StyledLoaderDiv>
        <AsyncLoader
          status={status}
          variant={LoaderVariantsEnum.GREY}
          shouldHideRetryAfterReconnect
          onRetryClick={onRetryClick}
          color={theme.greys.shade90}
        />
      </StyledLoaderDiv>
    </>
  );
};
